/**
 * How much of the board profile each drawn board shows.
 *
 * The Wall&CeilingBoard profile is 17.25" deep but courses step by 16": the
 * extra 1.25" is the tongue, tucked behind the next course. So a board is drawn
 * at its full profile depth wherever another board sits directly beyond it (the
 * overlap is what makes the courses read as snug), and cut back to its own
 * depth wherever nothing does — the last course of a section, or a board with
 * a SlatWall inset, a window or bare wall beyond it — so it never pokes out
 * past its own edge.
 *
 * Two more conditions, both from boards cut around windows and doors:
 *   - the board beyond must cover this one's WHOLE length — a tongue left on
 *     where only part of the board is covered pokes out into the opening;
 *   - only a full-depth board has a tongue to keep. A piece cut narrower (the
 *     sliver of a strip left beside a door) is drawn at its own depth, never
 *     stretched out to a whole board's.
 *
 * For a plain section this is exactly the old rule ("every course keeps the
 * overlap, the last one is clamped to the section's edge"); it just also gets
 * notched and interrupted courses right.
 */

import { BoardRect } from './types';
import { PANEL_WIDTH_FEET } from '../../../calculations/constants';

const TOUCH = 1e-4;

/** Does the board run up the wall (a strip) rather than along it (a row)? */
export function isVerticalBoard(b: BoardRect): boolean {
  return b.vertical ?? b.width < b.height;
}

/**
 * Depth to draw `board` at, in feet. `boards` is every board drawn with it (the
 * same section and direction); `fullDepth` is the profile's full depth.
 */
export function boardCoverage(board: BoardRect, boards: BoardRect[], vertical: boolean, fullDepth: number): number {
  // "across" is the direction courses stack in; "along" is the way the board runs.
  const acrossStart = (b: BoardRect) => (vertical ? b.x : b.y);
  const acrossSize = (b: BoardRect) => (vertical ? b.width : b.height);
  const alongStart = (b: BoardRect) => (vertical ? b.y : b.x);
  const alongSize = (b: BoardRect) => (vertical ? b.height : b.width);

  const end = acrossStart(board) + acrossSize(board);
  const a0 = alongStart(board);
  const a1 = a0 + alongSize(board);

  // A piece cut narrower than a course has no tongue left to tuck in.
  if (acrossSize(board) < PANEL_WIDTH_FEET - 1e-3) return acrossSize(board);

  // The stretches of this board's length with a board directly beyond them —
  // they must add up to all of it.
  const covered = boards
    .filter((o) => o !== board && Math.abs(acrossStart(o) - end) < TOUCH)
    .map((o): [number, number] => [Math.max(a0, alongStart(o)), Math.min(a1, alongStart(o) + alongSize(o))])
    .filter(([from, to]) => to - from > TOUCH)
    .sort((p, q) => p[0] - q[0]);
  let reach = a0;
  for (const [from, to] of covered) {
    if (from > reach + TOUCH) break;
    reach = Math.max(reach, to);
  }
  if (reach < a1 - TOUCH) return acrossSize(board);

  const boundary = Math.max(...boards.map((b) => acrossStart(b) + acrossSize(b)));
  return Math.min(acrossStart(board) + fullDepth, boundary) - acrossStart(board);
}

/**
 * The stretches along `board`'s length (absolute, in its run's axis) with a
 * board directly beyond it, merged; and the rest. A board covered all the
 * way is one covered stretch. Where neighbours step in height — the boards
 * of a gable under a sloped ceiling — a board is covered part of its length
 * only: it keeps its full profile (and so a hairline joint) along the
 * covered stretch, and is cut back to its own depth only where nothing
 * covers it. (Cutting the whole board back — by squashing its profile, as it
 * once was — opened a 1.3" gap at every joint of a gable.)
 */
export function coverageStretches(board: BoardRect, boards: BoardRect[], vertical: boolean): { from: number; to: number; covered: boolean }[] {
  const acrossStart = (b: BoardRect) => (vertical ? b.x : b.y);
  const acrossSize = (b: BoardRect) => (vertical ? b.width : b.height);
  const alongStart = (b: BoardRect) => (vertical ? b.y : b.x);
  const alongSize = (b: BoardRect) => (vertical ? b.height : b.width);
  const end = acrossStart(board) + acrossSize(board);
  const a0 = alongStart(board);
  const a1 = a0 + alongSize(board);
  const covered = boards
    .filter((o) => o !== board && Math.abs(acrossStart(o) - end) < TOUCH)
    .map((o): [number, number] => [Math.max(a0, alongStart(o)), Math.min(a1, alongStart(o) + alongSize(o))])
    .filter(([from, to]) => to - from > TOUCH)
    .sort((p, q) => p[0] - q[0]);
  const merged: [number, number][] = [];
  for (const [from, to] of covered) {
    const last = merged[merged.length - 1];
    if (last && from <= last[1] + TOUCH) last[1] = Math.max(last[1], to);
    else merged.push([from, to]);
  }
  const out: { from: number; to: number; covered: boolean }[] = [];
  let at = a0;
  for (const [from, to] of merged) {
    if (from > at + TOUCH) out.push({ from: at, to: from, covered: false });
    out.push({ from: Math.max(at, from), to, covered: true });
    at = to;
  }
  if (at < a1 - TOUCH) out.push({ from: at, to: a1, covered: false });
  return out;
}

/** The furthest edge of any board across the courses: a wall's (or section's) end, which no tongue may pass. */
export function coverageBoundary(boards: BoardRect[], vertical: boolean): number {
  return Math.max(...boards.map((b) => (vertical ? b.x + b.width : b.y + b.height)));
}
